<template>
    <div class="createShareModal">
        <StepByStep
            ref="stepByStepRef"
            :steps="steps"
            :validation-rules="validationRules"
            :completed-steps="completedSteps"
            @step-change="onStepChange"
            @finish="handleCreate"
            @mark-completed="handleMarkCompleted"
        >
            <!-- Step 1: Informações Básicas -->
            <template #step-0="{ nextStep, canNext }">
                <div class="step-content">
                    <div class="step-body">
                        <div class="modal-section">
                            <h4>Informações da Página</h4>

                            <div class="row mb_20">
                                <!-- Título da página -->
                                <div class="modal-field grid_col_12">
                                    <sw-input
                                        v-model="formData.title"
                                        label="Título da página *"
                                        description="Ex: Documentos Pessoais - João Silva"
                                        required
                                    />
                                </div>
                            </div>

                            <div class="row mb_20">
                                <!-- Descrição -->
                                <div class="modal-field grid_col_12">
                                    <sw-input
                                        v-model="formData.description"
                                        label="Descrição"
                                        description="Ex: Acesso aos meus documentos pessoais organizados"
                                        type="textarea"
                                        :rows="3"
                                    />
                                </div>
                            </div>
                        </div>

                        <div class="modal-section">
                            <h4>Personalização da URL</h4>

                            <div class="row mb_20">
                                <!-- URL personalizada -->
                                <div class="modal-field grid_col_12">
                                    <!-- Preview da URL completa -->
                                    <div class="createShareModal_urlPreview">
                                        <span class="createShareModal_urlBase">https://share.filesave.com.br/</span>
                                        <span class="createShareModal_urlCompany">{{ companySlug }}/</span>
                                        <span v-if="formData.separateByClient && formData.selectedClient" class="createShareModal_urlClient">{{ formData.selectedClient }}/</span>
                                        <span class="createShareModal_urlCustom">{{ formData.customUrl || generateUrlFromTitle() || 'sua-url-personalizada' }}</span>
                                    </div>

                                    <!-- Aviso sobre hash de segurança -->
                                    <AlertMessage
                                        type="success"
                                        icon="shield"
                                        title="Proteção Adicional"
                                        description="Após a criação, será adicionado automaticamente um hash único de segurança à URL para aumentar a proteção. O hash será gerado pelo sistema e você só saberá qual é após finalizar a criação."
                                    >
                                        <!-- Exemplo de URL com hash -->
                                        <div class="createShareModal_securityExample">
                                            <strong>Exemplo:</strong>
                                            <div class="createShareModal_securityExample_url">
                                                <span class="createShareModal_urlBase">https://share.filesave.com.br/</span>
                                                <span class="createShareModal_urlCompany">{{ companySlug }}/</span>
                                                <span class="createShareModal_urlHash">abc-123/</span>
                                                <span v-if="formData.separateByClient && formData.selectedClient" class="createShareModal_urlClient">{{ formData.selectedClient }}/</span>
                                                <span class="createShareModal_urlCustom">{{ formData.customUrl || generateUrlFromTitle() || 'documento-123' }}</span>
                                            </div>
                                            <em class="createShareModal_securityExample_note">* Exemplo ilustrativo - o hash real será diferente</em>
                                        </div>
                                    </AlertMessage>
                                    
                                    <sw-input
                                        v-model="formData.customUrl"
                                        label="URL personalizada (opcional)"
                                        formatSearch=" "
                                        formatReplace="-"
                                        description="Deixe em branco para gerar automaticamente"
                                        lower
                                        class="mt_30"
                                    />
                                </div>
                            </div>

                            <div class="row">
                                <!-- Checkbox para separar por cliente -->
                                <div class="modal-field grid_col_6">
                                    <sw-checkbox
                                        v-model="formData.separateByClient"
                                        label="Separar por cliente"
                                    />
                                </div>

                                <!-- Select de clientes -->
                                <div v-if="formData.separateByClient" class="modal-field grid_col_6">
                                    <sw-select
                                        v-model="formData.selectedClient"
                                        label="Selecione o cliente *"
                                        :options="clientOptions"
                                        search
                                        search-icon="search"
                                        placeholder="Escolha um cliente"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="step-actions">
                        <div class="step-actions_spacer"></div>
                        <sw-button
                            name="Continuar"
                            color="blue"
                            :disabled="!canNext"
                            @click="nextStep"
                        />
                    </div>
                </div>
            </template>

            <!-- Step 2: Configurações de Acesso -->
            <template #step-1="{ nextStep, prevStep, canNext }">
                <div class="step-content">
                    <div class="step-body">
                        <div class="modal-section">
                            <h4>Status da Página</h4>

                            <div class="row mb_20">
                                <!-- Status Online -->
                                <div class="modal-field grid_col_2">
                                    <sw-radio
                                        v-model="formData.status"
                                        label="Online"
                                        :value="WEB_LINK_STATUS.ACTIVE"
                                        name="pageStatus"
                                    />
                                </div>

                                <!-- Status Offline -->
                                <div class="modal-field grid_col_2">
                                    <sw-radio
                                        v-model="formData.status"
                                        label="Offline"
                                        :value="WEB_LINK_STATUS.INACTIVE"
                                        name="pageStatus"
                                    />
                                </div>
                            </div>
                        </div>

                        <div class="modal-section">
                            <h4>Controle de Acesso</h4>

                            <div class="row mb_30">
                                <!-- Tipo de acesso -->
                                <div class="modal-field grid_col_12">
                                    <div class="createShareModal_accessOptions">
                                        <div
                                            class="createShareModal_accessOption"
                                            :class="{ active: formData.accessType === 'public' }"
                                            @click="formData.accessType = 'public'"
                                        >
                                            <div class="createShareModal_accessIcon">
                                                <sw-icon name="earth" />
                                            </div>
                                            <div class="createShareModal_accessInfo">
                                                <h4>Público</h4>
                                                <p>Qualquer pessoa com o link pode acessar</p>
                                            </div>
                                        </div>
                                        <div
                                            class="createShareModal_accessOption"
                                            :class="{ active: formData.accessType === 'protected' }"
                                            @click="formData.accessType = 'protected'"
                                        >
                                            <div class="createShareModal_accessIcon">
                                                <sw-icon name="lock" />
                                            </div>
                                            <div class="createShareModal_accessInfo">
                                                <h4>Protegido</h4>
                                                <p>Requer email e senha para acessar</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Email e Senha (se protegido) -->
                            <div v-if="formData.accessType === 'protected'" class="row mb_20">
                                <div class="modal-field grid_col_6 grid_col_s_12">
                                    <sw-input
                                        v-model="formData.email"
                                        label="Email de acesso *"
                                        type="email"
                                        description="Digite o email"
                                        required
                                    />
                                </div>
                                <div class="modal-field grid_col_6 grid_col_s_12">
                                    <sw-input
                                        v-model="formData.password"
                                        label="Senha de acesso *"
                                        type="password"
                                        description="Digite a senha"
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        <div class="modal-section">
                            <h4>Configurações de Validade</h4>

                            <div class="row mb_20">
                                <!-- Checkbox para expiração -->
                                <div class="modal-field grid_col_6">
                                    <sw-checkbox
                                        v-model="formData.hasExpiry"
                                        label="Definir data de expiração"
                                    />
                                </div>

                                <!-- Data de expiração -->
                                <div v-if="formData.hasExpiry" class="modal-field grid_col_6 grid_col_s_12">
                                    <sw-input
                                        v-model="formData.expiryDate"
                                        label="Data de expiração *"
                                        :dateView="true"
                                        :minDate="moment().toDate()"
                                    />
                                </div>

                                <!-- Alert sobre expiração -->
                                <AlertMessage
                                    v-if="formData.hasExpiry && formData.expiryDate"
                                    :variant="ALERT_VARIANTS.INFO"
                                    :description="expiryAlertMessage"
                                />
                            </div>

                            <div class="row mb_20">
                                <!-- Checkbox para excluir após download -->
                                <div class="modal-field grid_col_12">
                                    <sw-checkbox
                                        v-model="formData.deleteAfterDownload"
                                        label="Excluir página após download"
                                        description="A página será automaticamente removida quando todos os arquivos forem baixados"
                                    />
                                </div>

                                <!-- Alert sobre exclusão após download -->
                                <AlertMessage
                                    v-if="formData.deleteAfterDownload"
                                    :variant="ALERT_VARIANTS.WARNING"
                                    icon="warning"
                                    title="Atenção!"
                                    description="Esta página será permanentemente excluída quando todos os documentos disponíveis forem baixados. Esta ação não pode ser desfeita."
                                />
                            </div>
                        </div>
                    </div>

                    <div class="step-actions">
                        <sw-button
                            name="Voltar"
                            color="gray"
                            @click="prevStep"
                        />
                        <div class="step-actions_spacer"></div>
                        <sw-button
                            name="Continuar"
                            color="blue"
                            :disabled="!canNext"
                            @click="nextStep"
                        />
                    </div>
                </div>
            </template>

            <!-- Step 3: Seleção de Documentos -->
            <template #step-2="{ nextStep, prevStep, canNext }">
                <div class="step-content">
                    <div class="step-body">
                        <div class="modal-section">
                            <h4>Arquivos Compartilhados</h4>
                            <p class="modal-section-description">
                                Selecione os arquivos que ficarão visíveis nesta página de compartilhamento.
                            </p>

                        <FileSelector
                            v-model:selected-documents="formData.selectedDocuments"
                            :available-documents="availableDocuments"
                            :company-options="documentClientOptions"
                            :category-options="documentCategoryOptions"
                        />
                        </div>
                    </div>

                    <div class="step-actions">
                        <sw-button
                            name="Voltar"
                            color="gray"
                            @click="prevStep"
                        />
                        <div class="step-actions_spacer"></div>
                        <sw-button
                            name="Continuar"
                            color="blue"
                            :disabled="!canNext"
                            @click="nextStep"
                        />
                    </div>
                </div>
            </template>

            <!-- Step 4: Revisar e Confirmar -->
            <template #step-3="{ prevStep }">
                <div class="step-content">
                    <div class="step-body">
                        <!-- Conteúdo de revisão (antes de criar) -->
                        <div v-if="!isPageCreated" class="review-summary">
                            <div class="review-section modal-section">
                                <h4 class="review-section_title">Informações Básicas</h4>
                                <div class="review-item">
                                    <span class="review-item_label">Título:</span>
                                    <span class="review-item_value">{{ formData.title }}</span>
                                </div>
                                <div v-if="formData.description" class="review-item">
                                    <span class="review-item_label">Descrição:</span>
                                    <span class="review-item_value">{{ formData.description }}</span>
                                </div>
                                <div class="review-item">
                                    <span class="review-item_label">URL:</span>
                                    <span class="review-item_value url-preview">{{ shareUrl }}</span>
                                </div>

                                <!-- Aviso sobre hash de segurança na revisão -->
                                <AlertMessage
                                    type="warning"
                                    description="A URL final terá um hash de segurança adicionado automaticamente pelo sistema após a criação. O link real será exibido quando você finalizar."
                                />
                            </div>

                            <div class="review-section modal-section">
                                <h4 class="review-section_title">Configurações de Acesso</h4>
                                <div class="review-item">
                                    <span class="review-item_label">Tipo:</span>
                                    <span class="review-item_value">{{ formData.accessType === 'public' ? 'Público' : 'Protegido por email e senha' }}</span>
                                </div>
                                <div class="review-item">
                                    <span class="review-item_label">Data de expiração:</span>
                                    <span class="review-item_value">
                                        {{ formData.hasExpiry ? formatDate(formData.expiryDate) : 'Sem data de expiração' }}
                                    </span>
                                </div>
                                <div class="review-item">
                                    <span class="review-item_label">Status:</span>
                                    <span class="review-item_value">{{ getStatusLabel(formData.status) }}</span>
                                </div>
                                <div class="review-item">
                                    <span class="review-item_label">Excluir após download completo:</span>
                                    <span class="review-item_value">{{ formData.deleteAfterDownload ? 'Sim' : 'Não' }}</span>
                                </div>
                            </div>

                            <div class="review-section modal-section">
                                <h4 class="review-section_title">Arquivos Selecionados</h4>
                                <div v-if="selectedDocumentsData.length > 0" class="createShareModal_selectedDocuments">
                                    <div
                                        v-for="document in selectedDocumentsData"
                                        :key="document.id"
                                        class="createShareModal_selectedDocument"
                                    >
                                        <sw-icon :name="document.icon" />
                                        <span>{{ document.name }}</span>
                                    </div>
                                </div>
                                <div v-else class="review-no-documents">
                                    <AlertMessage
                                        type="warning"
                                        icon="folder"
                                        title="Nenhum arquivo selecionado"
                                        description="Esta página será criada sem arquivos. Você poderá adicionar arquivos posteriormente através da edição da página."
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="!isPageCreated" class="step-actions">
                        <sw-button
                            name="Voltar"
                            color="gray"
                            @click="prevStep"
                        />
                        <div class="step-actions_spacer"></div>
                        <sw-button
                            name="Criar Página"
                            color="green"
                            :disabled="!isFormValid"
                            @click="handleCreate"
                        />
                    </div>

                    <!-- Layout de sucesso após criar a página -->
                    <div v-else class="success-layout">
                        <div class="success-content">
                            <div class="success-icon">
                                <sw-icon name="checkmark" />
                            </div>
                            <h3>Página Criada com Sucesso!</h3>
                            <p>Sua página de compartilhamento foi criada e está pronta para ser utilizada.</p>

                            <div class="success-url">
                                <label>URL da Página:</label>
                                <div class="url-display">
                                    <span>{{ finalUrlWithHash }}</span>
                                    <div class="url-actions">
                                        <sw-tooltip content="Visualizar página">
                                            <app-copy-button :text-to-copy="finalUrlWithHash" />
                                        </sw-tooltip>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="view-page-action">
                            <sw-button
                                name="Visualizar Página"
                                hover-color="blue"
                                icon="earth"
                                @click="viewPage"
                            />
                        </div>

                        <div class="success-actions">
                            <sw-button
                                name="Criar Nova Página"
                                color="green"
                                @click="resetForm"
                            />
                            <sw-button
                                name="Fechar"
                                @click="$emit('close')"
                            />
                        </div>
                    </div>
                </div>
            </template>
        </StepByStep>
    </div>
</template>

<script setup>
    import { ref, computed, onMounted, watch } from 'vue';
    import { useStore } from 'vuex';
    import moment from 'moment';
    import StepByStep from '@/components/steps/StepByStep.vue';
    import AlertMessage from '@/components/alerts/AlertMessage.vue';
    import EmptyState from '@/components/empty-states/EmptyState.vue';
    import FileSelector from '@/components/files/FileSelector.vue';
    import { loadingFull, clearAllNotify } from 'sysweb-vue-components/src/global';
    import { WEB_LINK_STATUS, ALERT_VARIANTS } from '@/constants';
    import { mockAvailableDocuments, mockClientOptions, mockDocumentClientOptions, mockDocumentCategoryOptions } from '@/data/mockData';

    const emit = defineEmits(['close', 'save']);
    const store = useStore();

    // Estados
    const isPageCreated = ref(false);
    const finalUrlWithHash = ref('');
    const completedSteps = ref([]);
    const stepByStepRef = ref(null);


    // Steps configuration
    const steps = ref([
        {
            title: 'Informações Básicas',
            subtitle: 'Título e descrição',
            description: 'Configure as informações principais da página'
        },
        {
            title: 'Configurações de Acesso',
            subtitle: 'Privacidade e expiração',
            description: 'Defina como a página será acessada'
        },
        {
            title: 'Seleção de Arquivos',
            subtitle: 'Conteúdo da página',
            description: 'Escolha quais arquivos serão visíveis'
        },
        {
            title: 'Revisar e Confirmar',
            subtitle: 'Resumo final',
            description: 'Revise todas as configurações antes de criar'
        }
    ]);

    // Form data
    const formData = ref({
        title: '',
        description: '',
        customUrl: '',
        separateByClient: false,
        selectedClient: '',
        accessType: 'public',
        email: '',
        password: '',
        hasExpiry: false,
        expiryDate: null,
        deleteAfterDownload: false,
        status: WEB_LINK_STATUS.ACTIVE,
        selectedDocuments: []
    });

    // Validation rules
    const validationRules = ref({
        0: () => {
            const titleValid = formData.value.title.trim() !== '';
            const clientValid = !formData.value.separateByClient || formData.value.selectedClient !== '';
            return titleValid && clientValid;
        }, // Step 1: Título obrigatório e cliente se marcado
        1: () => {
            // Step 2: Tipo de acesso obrigatório, email e senha se protegido, data se checkbox marcado
            const accessValid = formData.value.accessType !== '';
            const emailValid = formData.value.accessType !== 'protected' || formData.value.email.trim() !== '';
            const passwordValid = formData.value.accessType !== 'protected' || formData.value.password.trim() !== '';
            const expiryValid = !formData.value.hasExpiry || (formData.value.expiryDate && formData.value.expiryDate !== '');
            return accessValid && emailValid && passwordValid && expiryValid;
        },
        2: () => true, // Step 3: Pode prosseguir sem documentos
        3: () => true // Step 4: Revisão sempre válido
    });

    // Opções de status
    const statusOptions = ref([
        { value: WEB_LINK_STATUS.ACTIVE, label: 'Online' },
        { value: WEB_LINK_STATUS.INACTIVE, label: 'Offline' }
    ]);

    // Mock de documentos disponíveis
    const availableDocuments = ref(mockAvailableDocuments);

    // Opções de clientes (mock)
    const clientOptions = ref(mockClientOptions);

    // Opções para filtro de clientes nos documentos
    const documentClientOptions = ref(mockDocumentClientOptions);

    // Opções para filtro de categorias nos documentos
    const documentCategoryOptions = ref([
        { value: '', label: 'Todas as categorias' },
        ...mockDocumentCategoryOptions
    ]);




    // Slug da empresa vindo das configurações do usuário
    const companySlug = computed(() => {
        return store.state.auth.settings?.slug_page_web || 'shared-company';
    });

    // URL completa da página
    const shareUrl = computed(() => {
        const url = formData.value.customUrl || generateUrlFromTitle();
        let fullUrl = `https://share.filesave.com.br/${companySlug.value}`;

        if (formData.value.separateByClient && formData.value.selectedClient) {
            fullUrl += `/${formData.value.selectedClient}`;
        }

        return `${fullUrl}/${url}`;
    });

    // Validação do formulário completo
    const isFormValid = computed(() => {
        const basicValid = formData.value.title.trim() !== '' &&
                          formData.value.accessType !== '';

        const passwordValid = formData.value.accessType !== 'protected' || formData.value.password.trim() !== '';
        const expiryValid = !formData.value.hasExpiry || (formData.value.expiryDate && formData.value.expiryDate !== '');

        return basicValid && passwordValid && expiryValid;
    });

    // Mensagem do alert de expiração com data formatada
    const expiryAlertMessage = computed(() => {
        if (!formData.value.expiryDate) return '';

        const formattedDate = moment(formData.value.expiryDate).format('DD/MM/YYYY');
        return `A página ficará indisponível em ${formattedDate}. Você pode alterar ou remover a expiração a qualquer momento.`;
    });

    // Documentos selecionados com dados completos
    const selectedDocumentsData = computed(() => {
        if (!formData.value.selectedDocuments || !availableDocuments.value) return [];

        return availableDocuments.value.filter(doc =>
            formData.value.selectedDocuments.includes(doc.id)
        );
    });



    // Funções
    const onStepChange = (newStep) => {
        console.log('Step changed to:', newStep);
    };

    const generateUrlFromTitle = (title = null) => {
        const titleToUse = title || formData.value.title;
        if (!titleToUse) return '';

        return titleToUse
            .toLowerCase()
            .replace(/[^a-z0-9\s]/g, '')
            .replace(/\s+/g, '-')
            .replace(/^-+|-+$/g, '');
    };


    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Intl.DateTimeFormat('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        }).format(moment(dateString).toDate());
    };

    const getStatusLabel = (status) => {
        const option = statusOptions.value.find(opt => opt.value === status);
        return option?.label || status;
    };

    // Watcher para atualizar URL personalizada baseada no título
    watch(() => formData.value.title, (newTitle) => {
        if (newTitle && newTitle.trim() !== '') {
            formData.value.customUrl = generateUrlFromTitle(newTitle);
        } else {
            formData.value.customUrl = '';
        }
    });

    // Função para gerar hash de segurança
    const generateSecurityHash = () => {
        const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
        let result = '';
        for (let i = 0; i < 7; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
            if (i === 2) result += '-'; // Formato: abc-1234
        }
        return result;
    };

    const handleCreate = async () => {
        if (!isFormValid.value) return;

        try {
            // Mostrar loading full screen
            loadingFull({
                text: 'Criando página de compartilhamento...',
                subText: 'Processando dados e gerando URL',
                enabled: true
            });

            // Simular envio para API (delay de 2 segundos)
            await new Promise(resolve => setTimeout(resolve, 2000));

            // Gerar hash de segurança
            const securityHash = generateSecurityHash();

            // Construir URL final com hash
            const url = formData.value.customUrl || generateUrlFromTitle();
            let finalUrl = `https://share.filesave.com.br/${companySlug.value}/${securityHash}`;

            if (formData.value.separateByClient && formData.value.selectedClient) {
                finalUrl += `/${formData.value.selectedClient}`;
            }

            finalUrl += `/${url}`;

            // Armazenar URL final e marcar como criado
            finalUrlWithHash.value = finalUrl;
            isPageCreated.value = true;

            const shareData = {
                id: Date.now(), // Mock ID
                title: formData.value.title,
                description: formData.value.description,
                shareUrl: finalUrl, // URL final com hash
                accessType: formData.value.accessType,
                email: formData.value.accessType === 'protected' ? formData.value.email : null,
                password: formData.value.accessType === 'protected' ? formData.value.password : null,
                expiryDate: formData.value.hasExpiry ? formData.value.expiryDate : null,
                deleteAfterDownload: formData.value.deleteAfterDownload,
                status: formData.value.status,
                documents: selectedDocumentsData.value,
                createdAt: moment().toISOString(),
                securityHash: securityHash, // Hash para referência
                stats: {
                    views: 0,
                    downloads: 0
                }
            };

            // Esconder loading
            loadingFull({
                text: '',
                subText: '',
                enabled: false
            });

            // Marcar o último step como completed
        if (stepByStepRef.value) {
            completedSteps.value.push(3); // Step 4 (index 3)
        }

        emit('save', shareData);

        } catch (error) {
            // Esconder loading em caso de erro
            loadingFull({
                text: '',
                subText: '',
                enabled: false
            });
            console.error('Erro ao criar página:', error);
            // Aqui você poderia mostrar uma notificação de erro
        }
    };

    // Visualizar página criada
    const viewPage = () => {
        window.open(finalUrlWithHash.value, '_blank');
    };

    // Gerenciar steps completados
    const handleMarkCompleted = (stepIndex) => {
        if (!completedSteps.value.includes(stepIndex)) {
            completedSteps.value.push(stepIndex);
        }
    };


    // Resetar formulário para criar nova página
    const resetForm = () => {
        // Reset do estado
        isPageCreated.value = false;
        finalUrlWithHash.value = '';
        completedSteps.value = [];

        // Reset dos dados do formulário
        formData.value = {
            title: '',
            description: '',
            customUrl: '',
            accessType: 'public',
            email: '',
            password: '',
            hasExpiry: false,
            expiryDate: null,
            deleteAfterDownload: false,
            status: WEB_LINK_STATUS.ACTIVE,
            selectedDocuments: [],
            separateByClient: false,
            selectedClient: ''
        };

        // Reset dos filtros - não há mais filtros locais
    };

</script>

<style lang="scss" scoped>
@import '@/assets/css/mediaScreen.scss';

.createShareModal {
    display: flex;
    flex-direction: column;
    gap: 1.5em;
    min-height: 500px;
}

/* Estilos das seções e campos já estão definidos globalmente em modalSections.scss */

.modal-field-label {
    font-size: 0.875em;
    font-weight: 600;
    color: var(--textPrimary, #1e293b);
    margin-bottom: 0.5em;
    display: block;
}

.createShareModal_urlPreview {
    margin-bottom: 0.75em;
    padding: 0.875em 1em;
    background: var(--backgroundInsideBox, #f8fafc);
    border: 1px solid var(--borderColorLight, #e5e7eb);
    border-radius: 0.5em;
    font-family: monospace;
    font-size: 0.875em;
    word-break: break-all;
    line-height: 1.4;
}

.createShareModal_urlBase {
    color: var(--textSecondary, #64748b);
    font-weight: 400;
}

.createShareModal_urlCompany {
    color: var(--colorBlue, #3b82f6);
    font-weight: 600;
}

.createShareModal_urlClient {
    color: var(--colorGreen, #10b981);
    font-weight: 600;
}

.createShareModal_urlCustom {
    color: var(--textPrimary, #1e293b);
    font-weight: 700;
    background: rgba(59, 130, 246, 0.1);
    padding: 0.125em 0.25em;
    border-radius: 0.25em;
}

.createShareModal_urlHash {
    color: var(--colorOrange, #f59e0b);
    font-weight: 600;
    background: rgba(245, 158, 11, 0.1);
    padding: 0.125em 0.25em;
    border-radius: 0.25em;
}

.createShareModal_securityExample {
    margin-top: 0.5em;
}

.createShareModal_securityExample strong {
    color: var(--textPrimary, #1e293b);
    font-weight: 600;
    display: block;
    margin-bottom: 0.5em;
}

.createShareModal_securityExample_url {
    padding: 0.5em;
    background: rgba(255, 255, 255, 0.7);
    border-radius: 0.25em;
    font-family: monospace;
    font-size: 0.9em;
    word-break: break-all;
    border: 1px dashed rgba(16, 185, 129, 0.3);
    margin-bottom: 0.5em;
}

.createShareModal_securityExample_note {
    font-size: 0.75em;
    color: var(--textSecondary, #64748b);
    font-style: italic;
    display: block;
}

.url-preview {
    color: var(--textSecondary, #64748b) !important;
}

.url-final {
    color: var(--colorGreen, #10b981) !important;
    font-weight: 600 !important;
    font-family: monospace !important;
    background: rgba(16, 185, 129, 0.1) !important;
    padding: 0.375em 0.5em !important;
    border-radius: 0.25em !important;
    border: 1px solid rgba(16, 185, 129, 0.2) !important;
    display: block;
    margin-top: 0.25em;
}

.createShareModal_accessOptions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1em;
    margin-top: 0.5em;
}

.createShareModal_accessOption {
    display: flex;
    align-items: center;
    gap: 1em;
    padding: 1.25em;
    border: 2px solid var(--borderColorLight, #e5e7eb);
    border-radius: 0.75em;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        border-color: var(--colorBlue, #3b82f6);
    }

    &.active {
        border-color: var(--colorBlue, #3b82f6);
        background: rgba(59, 130, 246, 0.05);
    }
}

.createShareModal_accessIcon {
    width: 2.5em;
    height: 2.5em;
    border-radius: 0.5em;
    background: linear-gradient(135deg, var(--colorBlue, #3b82f6), var(--colorBlueDark, #1d4ed8));
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25em;
    flex-shrink: 0;
}

.createShareModal_accessInfo {
    h4 {
        margin: 0 0 0.25em 0;
        font-size: 1em;
        font-weight: 600;
        color: var(--textPrimary, #1e293b);
    }

    p {
        margin: 0;
        font-size: 0.875em;
        color: var(--textSecondary, #64748b);
        line-height: 1.4;
    }
}

.createShareModal_documentSection {
    display: flex;
    flex-direction: column;
    gap: 1.5em;
}

.createShareModal_documentHeader {
    h4 {
        margin: 0 0 0.5em 0;
        font-size: 1.125em;
        font-weight: 600;
        color: var(--textPrimary, #1e293b);
    }

    p {
        margin: 0;
        font-size: 0.875em;
        color: var(--textSecondary, #64748b);
        line-height: 1.5;
    }
}

.createShareModal_documentList {
    display: flex;
    flex-direction: column;
    gap: 0.75em;
    max-height: 300px;
    overflow-y: auto;
    padding: 0.5em;
    border: 1px solid var(--borderColorLight, #e5e7eb);
    border-radius: 0.75em;
    background: var(--backgroundInsideBox, #f8fafc);
}

.createShareModal_documentItem {
    display: flex;
    align-items: center;
    gap: 1em;
    padding: 1em;
    background: white;
    border: 1px solid var(--borderColorLight, #e5e7eb);
    border-radius: 0.5em;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        border-color: var(--colorBlue, #3b82f6);
        box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
    }

    &.selected {
        border-color: var(--colorBlue, #3b82f6);
        background: rgba(59, 130, 246, 0.05);
    }
}

.createShareModal_documentCheckbox {
    .sw-icon {
        font-size: 1.25em;
        color: var(--borderColorLight, #e5e7eb);
        transition: all 0.2s ease;

        &.checked {
            color: var(--colorBlue, #3b82f6);
        }
    }
}

.createShareModal_documentIcon {
    width: 2em;
    height: 2em;
    border-radius: 0.375em;
    background: linear-gradient(135deg, var(--colorBlue, #3b82f6), var(--colorBlueDark, #1d4ed8));
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1em;
    flex-shrink: 0;
}

.createShareModal_documentInfo {
    flex: 1;
    min-width: 0;

    h5 {
        margin: 0 0 0.25em 0;
        font-size: 0.875em;
        font-weight: 600;
        color: var(--textPrimary, #1e293b);
        line-height: 1.2;
    }

    span {
        font-size: 0.75em;
        color: var(--textSecondary, #64748b);
        line-height: 1.3;
    }
}

.createShareModal_documentStatus {
    span {
        padding: 0.25em 0.75em;
        border-radius: 1em;
        font-size: 0.75em;
        font-weight: 500;

        &.status-active {
            background: rgba(16, 185, 129, 0.1);
            color: var(--colorGreen, #10b981);
        }

        &.status-expiring {
            background: rgba(245, 158, 11, 0.1);
            color: var(--colorOrange, #f59e0b);
        }

        &.status-expired {
            background: rgba(239, 68, 68, 0.1);
            color: var(--colorRed, #ef4444);
        }
    }
}

.createShareModal_selectedCount {
    text-align: center;
    font-size: 0.875em;
    color: var(--textSecondary, #64748b);
    padding: 0.75em;
    background: white;
    border: 1px solid var(--borderColorLight, #e5e7eb);
    border-radius: 0.5em;
}

.createShareModal_selectedDocuments {
    margin-top: 1.5em;
    border: 1px solid var(--borderColorLight, #e5e7eb);
    border-radius: 0.75em;
    background: var(--backgroundInsideBox, #f8fafc);

    h5 {
        margin: 0;
        padding: 1em 1.25em 0.5em;
        font-size: 0.875em;
        font-weight: 600;
        color: var(--textPrimary, #1e293b);
        text-transform: uppercase;
        letter-spacing: 0.025em;
        border-bottom: 1px solid var(--borderColorLight, #e5e7eb);
    }
}

.createShareModal_selectedDocumentsList {
    padding: 0.75em;
    display: flex;
    flex-direction: column;
    gap: 0.5em;
}

.createShareModal_selectedDocumentItem {
    display: flex;
    align-items: center;
    gap: 0.75em;
    padding: 0.75em;
    background: white;
    border: 1px solid var(--borderColorLight, #e5e7eb);
    border-radius: 0.5em;
    transition: all 0.2s ease;

    &:hover {
        border-color: var(--colorBlue, #3b82f6);
        box-shadow: 0 2px 4px rgba(59, 130, 246, 0.1);
    }
}

.createShareModal_selectedDocumentIcon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.25em;
    height: 2.25em;
    background: linear-gradient(135deg, var(--colorBlue, #3b82f6), var(--colorBlueDark, #1d4ed8));
    color: white;
    border-radius: 0.5em;
    font-size: 1em;
    flex-shrink: 0;
}

.createShareModal_selectedDocumentInfo {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.25em;
}

.createShareModal_selectedDocumentName {
    font-size: 0.875em;
    font-weight: 500;
    color: var(--textPrimary, #1e293b);
    line-height: 1.3;
}

.createShareModal_selectedDocumentDetails {
    font-size: 0.75em;
    color: var(--textSecondary, #64748b);
}

.createShareModal_selectedDocumentRemove {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.75em;
    height: 1.75em;
    background: var(--colorRed, #ef4444);
    color: white;
    border-radius: 0.375em;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 0.875em;

    &:hover {
        background: var(--colorRedDark, #dc2626);
        transform: scale(1.05);
    }
}

.createShareModal_selectedDocument {
    display: flex;
    align-items: center;
    gap: 0.75em;
    padding: 0.75em;
    background: white;
    border: 1px solid var(--borderColorLight, #e5e7eb);
    border-radius: 0.5em;
    font-size: 0.875em;

    .sw-icon {
        color: var(--colorBlue, #3b82f6);
        font-size: 1em;
    }

    span {
        color: var(--textPrimary, #1e293b);
        font-weight: 500;
    }
}

/* ====================================
   LAYOUT DE SUCESSO
   ==================================== */
.success-layout {
    display: flex;
    flex-direction: column;
    gap: 2em;
    padding: 2em 0;
    text-align: center;
}

.success-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.5em;
}

.success-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 4em;
    height: 4em;
    background: linear-gradient(135deg, var(--colorGreen, #10b981), var(--colorGreenDark, #059669));
    color: white;
    border-radius: 50%;
    font-size: 2em;
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.success-content h3 {
    font-size: 1.5em;
    font-weight: 600;
    color: var(--textPrimary, #1e293b);
    margin: 0;
}

.success-content p {
    font-size: 1em;
    color: var(--textSecondary, #64748b);
    margin: 0;
    max-width: 400px;
    line-height: 1.6;
}

.success-url {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 0.75em;
    margin-top: 1em;

    label {
        font-size: 0.875em;
        font-weight: 600;
        color: var(--textPrimary, #1e293b);
        text-align: left;
    }
}

.url-display {
    display: flex;
    align-items: center;
    gap: 0.75em;
    padding: 1em;
    background: var(--backgroundLight, #f8fafc);
    border: 1px solid var(--borderColorLight, #e5e7eb);
    border-radius: 0.5em;

    span {
        flex: 1;
        font-size: 0.875em;
        color: var(--textPrimary, #1e293b);
        font-family: monospace;
        text-align: left;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
}

.url-actions {
    display: flex;
    align-items: center;
    gap: 0.5em;
}

.view-page-action {
    display: flex;
    justify-content: center;
    margin: 1.5em 0 1em 0;
}

.success-actions {
    display: flex;
    align-items: center;
    gap: 1em;
    justify-content: center;
    margin-top: 1em;
}

/* Responsividade */
@include screen_medias(s, m) {
    .success-layout {
        padding: 1.5em 0;
        gap: 1.5em;
    }

    .success-icon {
        width: 3em;
        height: 3em;
        font-size: 1.5em;
    }

    .success-content h3 {
        font-size: 1.25em;
    }

    .success-content p {
        font-size: 0.9em;
    }

    .url-display {
        flex-direction: column;
        align-items: stretch;
        gap: 0.5em;

        span {
            text-align: center;
        }
    }

    .success-actions {
        flex-direction: column;
        gap: 0.75em;

        .step-actions_spacer {
            display: none;
        }
    }
}

.step-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 1.5em;
    border-top: 1px solid var(--borderColorLight, #e5e7eb);
    margin-top: 1.5em;
}

.step-actions_spacer {
    flex: 1;
}

.review-summary {
    display: flex;
    flex-direction: column;
    gap: 2em;
}

.review-section {
    padding: 1.5em;
    background: var(--backgroundCard, #f8fafc);
    border: 1px solid var(--borderColorLight, #e5e7eb);
    border-radius: 0.75em;

    &_title {
        margin: 0 0 1em 0;
        font-size: 1.125em;
        font-weight: 600;
        color: var(--textPrimary, #1e293b);
        border-bottom: 1px solid var(--borderColorLight, #e5e7eb);
        padding-bottom: 0.5em;
    }
}

.review-item {
    display: flex;
    align-items: flex-start;
    margin-bottom: 0.75em;

    &:last-child {
        margin-bottom: 0;
    }

    &_label {
        font-weight: 600;
        color: var(--textSecondary, #64748b);
        margin-right: 0.5em;
        font-size: 0.875em;
    }

    &_value {
        color: var(--textPrimary, #1e293b);
        flex: 1;
        font-size: 0.875em;
        word-break: break-word;
    }
}

@include screen_medias(s, m) {
    .createShareModal_accessOptions {
        grid-template-columns: 1fr;
    }

    .createShareModal_accessOption {
        padding: 1em;
    }

    .createShareModal_documentList {
        max-height: 250px;
    }

    .step-actions {
        flex-direction: column;
        gap: 1em;

        .step-actions_spacer {
            display: none;
        }
    }

    .review-item {
        flex-direction: column;
        gap: 0.5em;

        &_label {
            min-width: auto;
            margin-right: 0;
        }
    }
}

// Estilos para loading e mensagem de seleção de pessoa
.loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 3em 1em;
    background: var(--backgroundInsideBox, #f8fafc);
    border: 1px solid var(--borderColorLight, #e5e7eb);
    border-radius: 0.75em;
}

.loading-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1em;

    p {
        margin: 0;
        color: var(--textSecondary, #64748b);
        font-size: 0.875em;
    }
}

.person-type-message {
    padding: 2em 1em;
}

.review-no-documents {
    padding: 1em;
}
</style>